import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import './CartPage.css';

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartSummary
  } = useCart();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const summary = getCartSummary();

  const handleCheckout = async () => {
    if (!user) {
      navigate('/profile?redirect=cart');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Call your Firebase Cloud Function to create checkout session
      const functionUrl = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 
        'https://createcheckoutsession-ofbdc4rmlq-uc.a.run.app';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: cartItems.map(item => ({
            id: item.id,
            title: item.title,
            artist: item.artist,
            price: item.price,
            quantity: item.quantity,
            type: item.type,
            image: item.image,
            genre: item.genre
          })),
          userId: user.uid,
          userEmail: user.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Checkout error: ${error.message}. Please try again.`);
      setIsProcessing(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <ShoppingBag size={64} />
            </div>
            <h2>Your cart is empty</h2>
            <p>Add some awesome posters to get started!</p>
            <button 
              className="shop-btn"
              onClick={() => navigate('/shop')}
            >
              Browse Posters
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <h1 className="cart-title">
            Your <span className="gradient-text">Cart</span>
          </h1>
          <button 
            className="clear-cart-btn"
            onClick={clearCart}
          >
            Clear Cart
          </button>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                <div className="item-image">
                  <img src={item.image} alt={item.title} />
                </div>
                
                <div className="item-details">
                  <h3 className="item-title">{item.title}</h3>
                  <p className="item-artist">{item.artist}</p>
                  <span className="item-type">{item.type}</span>
                </div>

                <div className="item-quantity">
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                    aria-label="Decrease quantity"
                  >
                    <Minus size={16} />
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    aria-label="Increase quantity"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                <div className="item-price">
                  ${(item.price * item.quantity).toFixed(2)}
                </div>

                <button
                  className="item-remove"
                  onClick={() => removeFromCart(item.id)}
                  aria-label="Remove item"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h3 className="summary-title">Order Summary</h3>
            
            <div className="summary-row">
              <span>Subtotal ({summary.itemCount} items)</span>
              <span>${summary.subtotal.toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span>Tax</span>
              <span>${summary.tax.toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span>Shipping</span>
              <span>
                {summary.shipping === 0 ? 'FREE' : `$${summary.shipping.toFixed(2)}`}
              </span>
            </div>

            {summary.shipping === 0 && summary.subtotal < 50 && (
              <div className="shipping-notice">
                All digital posters are delivered instantly via email!
              </div>
            )}

            <div className="summary-divider"></div>

            <div className="summary-total">
              <span>Total</span>
              <span>${summary.total.toFixed(2)}</span>
            </div>

            <button 
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Proceed to Checkout'}
              {!isProcessing && <ArrowRight size={20} />}
            </button>

            {!user && (
              <p className="login-notice">
                Please log in to complete your purchase
              </p>
            )}

            <div className="secure-checkout">
              <p>ðŸ”’ Secure checkout powered by Stripe</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;